import React from "react";
import { motion } from "framer-motion";

const GrowthTools = () => {
  const tools = [
    {
      id: 1,
      title: "AI Agent Sandbox",
      desc: "Discover model strengths early. Log autonomous milestones, explore agent credentials, and build a verified portfolio long before staging integrations.",
      icon: (
        <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v1.244c0 .59-.517 1.025-1.096 1.025-1.186 0-2.316.326-3.298.896-.532.308-1.162-.057-1.162-.676v-1.24c0-.589.516-1.025 1.096-1.025a11.118 11.118 0 014.298.676zM14.25 3.104v1.244c0 .59.517 1.025 1.096 1.025a11.118 11.118 0 014.298-.676c.58 0 1.096.436 1.096 1.025v1.24c0 .619-.63.984-1.162.676a11.123 11.123 0 00-3.298-.896c-.579 0-1.096-.436-1.096-1.025zM3.104 9.75h1.244c.59 0 1.025.517 1.025 1.096 0 1.186.326 2.316.896 3.298.308.532-.057 1.162-.676 1.162h-1.24c-.589 0-1.025-.516-1.025-1.096a11.118 11.118 0 01.676-4.298zM3.104 14.25h1.244c.59 0 1.025-.517 1.025-1.096a11.122 11.122 0 00-.896-3.298c-.308-.532.057-1.162.676-1.162h1.24c.589 0 1.025.516 1.025 1.096a11.118 11.118 0 01-.676 4.298z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 2,
      title: "Intelligent Matchmaking",
      desc: "Move beyond generic API bindings. Receive hyper-personalized recommendations mapped directly to your cost constraints, security thresholds, and business goals.",
      icon: (
        <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
      )
    },
    {
      id: 3,
      title: "Compute Budget Navigator",
      desc: "Demystify API and GPU compute costs. Safely track complex pipeline utilization, forecast compute budgets, and match with cost-effective serverless nodes.",
      icon: (
        <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z" />
        </svg>
      )
    },
    {
      id: 4,
      title: "Active Dashboard Sync",
      desc: "Connect your daily operations. Deep integrations with third-party platforms (databases, CRMs, APIs) pull your business data directly into your predictive workspace.",
      icon: (
        <svg className="w-8 h-8 text-forest" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 12l-3-3m3 3l3-3m3-7h10m0 0l-3-3m3 3l-3 3M17 20v-8m0 8l-3-3m3 3l3-3" />
        </svg>
      )
    }
  ];

  return (
    <section className="py-16 px-6 max-w-7xl mx-auto text-zinc-800 dark:text-zinc-200">
      {/* Title */}
      <div className="mb-12">
        <h2 className="text-3xl font-extrabold text-zinc-900 dark:text-white mb-3 tracking-tight">
          Tools engineered for your continuous growth.
        </h2>
        <p className="text-zinc-500 dark:text-zinc-400 text-sm max-w-2xl font-light leading-relaxed">
          From initial discovery and strategy audits to production deployments, JL equips you with targeted, intelligent infrastructure at every step of your trajectory.
        </p>
      </div>

      {/* Row of Items */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {tools.map((t) => (
          <div key={t.id} className="space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100/60 dark:bg-emerald-950/20 border border-emerald-200/20 flex items-center justify-center">
              {t.icon}
            </div>
            <h3 className="text-lg font-bold text-zinc-900 dark:text-white">{t.title}</h3>
            <p className="text-xs font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
              {t.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default GrowthTools;
